import { AnimatePresence } from 'framer-motion';
import React, { lazy, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import Layout from './components/Layout';
import { Page } from './components/motion';
import PageLoader from './components/PageLoader';
import ScrollToTop from './components/ScrollToTop';
import { useAccessibilityFix } from './hooks/useAccessibilityFix';
import { useLanguagePersistence } from './hooks/useLanguagePersistence';
import { getUserInfo, refreshToken } from './store/slices/authSlice';

// Lazy load page components for code splitting
const Account = lazy(() => import('./pages/Account'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const ChatComponent = lazy(() => import('./pages/ChatComponent'));
const CompanyManagement = lazy(() => import('./pages/CompanyManagement'));
const CompanyPortal = lazy(() => import('./pages/CompanyPortal'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const GDPR = lazy(() => import('./pages/GDPR'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PDFManagement = lazy(() => import('./pages/PDFManagement'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const QALogs = lazy(() => import('./pages/QALogs'));
const Reports = lazy(() => import('./pages/Reports'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const VectorStoreManagement = lazy(
  () => import('./pages/VectorStoreManagement')
);

const ProtectedRoute = React.memo(({ children, allowedRoles = [] }) => {
  const { user, isAuthenticated } = useSelector(state => state.auth);

  // Redirect if not authenticated or user doesn't have required role
  if (
    !isAuthenticated ||
    !user ||
    (allowedRoles.length > 0 && !allowedRoles.includes(user.role))
  ) {
    return <Navigate to='/' replace />;
  }

  return <Layout>{children}</Layout>;
});

const AppRoutes = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const [authInitialized, setAuthInitialized] = React.useState(false);

  // Initialize language persistence
  useLanguagePersistence();

  // Fix accessibility issues with AnimatePresence
  useAccessibilityFix();

  // Initialize authentication on app startup
  useEffect(() => {
    if (authInitialized) return;

    const initializeAuth = async () => {
      const refreshTokenStr = localStorage.getItem('refresh_token');
      const accessTokenStr = localStorage.getItem('access_token');

      try {
        // Strategy: Always try to refresh if we have refresh token
        // This ensures token is fresh on app load
        if (refreshTokenStr) {
          await dispatch(refreshToken()).unwrap();
          // After successful refresh, get user info
          await dispatch(getUserInfo()).unwrap();
        } else if (accessTokenStr) {
          // If we have access token but no refresh token, try to get user info
          await dispatch(getUserInfo()).unwrap();
        }
      } catch (error) {
        // Errors are handled by authSlice - it will logout on auth failures
        // Only log for debugging, don't block initialization
        console.error('Auth initialization error:', error);
      } finally {
        setAuthInitialized(true);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, dispatch]);

  // Show loading while checking authentication (only if we have tokens)
  if (
    !authInitialized &&
    (localStorage.getItem('access_token') ||
      localStorage.getItem('refresh_token'))
  ) {
    return <PageLoader />;
  }

  if (!isAuthenticated || !user) {
    return (
      <>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode='wait' initial={false}>
            <Routes location={location} key={location.pathname}>
              <Route
                path='/'
                element={
                  <Page>
                    <LandingPage />
                  </Page>
                }
              />
              <Route
                path='/privacy-policy'
                element={
                  <Page>
                    <PrivacyPolicy />
                  </Page>
                }
              />
              <Route
                path='/terms-of-service'
                element={
                  <Page>
                    <TermsOfService />
                  </Page>
                }
              />
              <Route
                path='/cookie-policy'
                element={
                  <Page>
                    <CookiePolicy />
                  </Page>
                }
              />
              <Route
                path='/gdpr'
                element={
                  <Page>
                    <GDPR />
                  </Page>
                }
              />
              <Route path='*' element={<Navigate to='/' replace />} />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode='wait' initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route
              path='/'
              element={
                <ProtectedRoute>
                  <Page>
                    {user.role === 'admin' ? (
                      <AdminDashboard />
                    ) : (
                      <CustomerDashboard />
                    )}
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/companies'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Page>
                    <CompanyManagement />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/users'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Page>
                    <UserManagement />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/documents'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Page>
                    <PDFManagement />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/qa-logs'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Page>
                    <QALogs />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/vectorstore'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Page>
                    <VectorStoreManagement />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/reports'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Page>
                    <AdminReports />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/my-reports'
              element={
                <ProtectedRoute>
                  <Page>
                    <Reports />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/chat'
              element={
                <ProtectedRoute>
                  <Page>
                    <ChatComponent />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/account'
              element={
                <ProtectedRoute>
                  <Page>
                    <Account />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route
              path='/company-portal'
              element={
                <ProtectedRoute
                  allowedRoles={['company_admin', 'company_user', 'admin']}
                >
                  <Page>
                    <CompanyPortal />
                  </Page>
                </ProtectedRoute>
              }
            />
            <Route path='*' element={<Navigate to='/' replace />} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </>
  );
};

function App() {
  return <AppRoutes />;
}

export default App;
